import React from 'react';

/**
 * A local copy of a value that shows changes at once and hands them on after
 * a pause.
 *
 * For controls whose every change costs a request — ticking three carriers in
 * quick succession should send one query, not three, and the page should not
 * flicker between them. The control reads and writes `draft`; `commit` runs
 * once the draft has been still for `delayMs`.
 *
 * If `value` changes from outside (a reset, a load), the draft follows it
 * without committing.
 */
export function useDebouncedCommit<T>(
  value: T,
  commit: (next: T) => void,
  delayMs: number = 300,
): [T, (next: T) => void] {
  const [draft, setDraft] = React.useState<T>(value);
  const pending = React.useRef(false);
  const commitRef = React.useRef(commit);
  commitRef.current = commit;

  React.useEffect(() => {
    pending.current = false;
    setDraft(value);
  }, [value]);

  React.useEffect(() => {
    if (!pending.current) return undefined;
    const timer = window.setTimeout(() => {
      pending.current = false;
      commitRef.current(draft);
    }, delayMs);
    return () => window.clearTimeout(timer);
  }, [draft, delayMs]);

  const update = React.useCallback((next: T) => {
    pending.current = true;
    setDraft(next);
  }, []);

  return [draft, update];
}
